import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MessageSquareWarning, Mail, Package, Clock, Send } from "lucide-react";
import { toast } from "sonner";

const REASONS = {
  damaged: "Beschädigt", wrong_item: "Falscher Artikel",
  missing_qty: "Fehlende Menge", other: "Sonstiges",
};
const STATUS = { received: "Eingegangen", in_progress: "In Bearbeitung", resolved: "Abgeschlossen" };
const STATUS_STYLE = {
  received: "bg-amber-100 text-amber-800",
  in_progress: "bg-blue-100 text-blue-800",
  resolved: "bg-green-100 text-green-700",
};
const FILTERS = [["all", "Alle"], ["received", "Eingegangen"], ["in_progress", "In Bearbeitung"], ["resolved", "Abgeschlossen"]];

function ComplaintImage({ imageId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true; let objUrl;
    api.get(`/complaints/image/${imageId}`, { responseType: "blob" })
      .then((r) => { if (active) { objUrl = URL.createObjectURL(r.data); setUrl(objUrl); } })
      .catch(() => {});
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [imageId]);
  if (!url) return <div className="h-20 w-20 rounded-lg bg-neutral-100 animate-pulse" />;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`complaint-image-${imageId}`}>
      <img src={url} alt="Reklamation" className="h-20 w-20 rounded-lg object-cover border border-[var(--border)] hover:opacity-80" />
    </a>
  );
}

function ComplaintCard({ c, onUpdated }) {
  const [status, setStatus] = useState(c.status || "received");
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (status !== c.status) payload.status = status;
      if (reply.trim()) payload.admin_reply = reply.trim();
      if (!Object.keys(payload).length) { toast.info("Keine Änderung"); setSaving(false); return; }
      const { data } = await api.patch(`/complaints/${c.id}`, payload);
      toast.success("Gespeichert · Kunde benachrichtigt");
      setReply("");
      onUpdated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-5 border border-[var(--border)] rounded-2xl" data-testid={`complaint-card-${c.number}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--pop-pink)]/10 flex items-center justify-center text-[var(--pop-pink)]">
            <MessageSquareWarning size={20} />
          </div>
          <div>
            <div className="font-bold flex items-center gap-2">
              {c.number}
              <span className="text-xs font-normal text-[var(--text-muted)]">· {REASONS[c.reason] || c.reason}</span>
            </div>
            <div className="text-xs text-[var(--text-muted)] flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1"><Package size={11} /> {c.order_number || "—"}</span>
              <span className="inline-flex items-center gap-1"><Mail size={11} /> {c.customer_name} ({c.customer_email || "—"})</span>
              <span className="inline-flex items-center gap-1"><Clock size={11} /> {(c.created_at || "").slice(0, 10)}</span>
            </div>
          </div>
        </div>
        <span className={`text-xs font-semibold rounded-full px-3 py-1.5 ${STATUS_STYLE[c.status] || "bg-neutral-100"}`}
              data-testid={`complaint-status-${c.number}`}>
          {STATUS[c.status] || c.status}
        </span>
      </div>

      {c.description && (
        <div className="mt-3 text-sm bg-[var(--surface)] border border-[var(--border)] rounded-xl p-3 whitespace-pre-wrap">
          {c.description}
        </div>
      )}

      {(c.images || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`complaint-images-${c.number}`}>
          {c.images.map((im) => <ComplaintImage key={im.image_id} imageId={im.image_id} />)}
        </div>
      )}

      {c.admin_reply && (
        <div className="mt-3 text-sm bg-blue-50 border border-blue-200 rounded-xl p-3">
          <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide mb-1">
            Antwort{c.admin_reply_by ? ` · ${c.admin_reply_by}` : ""}{c.admin_reply_at ? ` · ${c.admin_reply_at.slice(0, 10)}` : ""}
          </div>
          <div className="whitespace-pre-wrap">{c.admin_reply}</div>
        </div>
      )}

      <div className="mt-4 border-t border-[var(--border)] pt-4 grid md:grid-cols-[180px_1fr] gap-3 items-start">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid={`complaint-status-select-${c.number}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="space-y-2">
          <textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)}
                    data-testid={`complaint-reply-${c.number}`}
                    placeholder="Antwort an den Kunden (optional) …"
                    className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 text-sm" />
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid={`complaint-save-${c.number}`}
                    className="rounded-full bg-[var(--pop-pink)] text-white font-bold">
              <Send size={14} className="mr-1" /> {saving ? "Speichere…" : "Speichern & benachrichtigen"}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function Complaints() {
  const [complaints, setComplaints] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/complaints", { params: filter === "all" ? {} : { status: filter } });
      setComplaints(data);
    } catch (e) { toast.error("Reklamationen konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const onUpdated = (updated) =>
    setComplaints((cur) => cur.map((x) => (x.id === updated.id ? updated : x))
      .filter((x) => filter === "all" || x.status === filter));

  return (
    <div data-testid="page-complaints">
      <PageHeader
        eyebrow="CRM"
        title="Reklamationen"
        description="Kundenreklamationen aus dem Portal zentral bearbeiten, Status pflegen und beantworten."
      />
      <div className="flex flex-wrap gap-2 mb-5" data-testid="complaints-filter">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`complaints-filter-${k}`}
                  className={`text-sm font-semibold rounded-full px-4 py-2 border transition-colors ${
                    filter === k ? "bg-[var(--pop-pink)] text-white border-[var(--pop-pink)]"
                                 : "bg-white text-[var(--text-muted)] border-[var(--border)] hover:border-[var(--pop-pink)]"}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[var(--text-muted)] py-16 text-center">Lade…</div>
      ) : complaints.length === 0 ? (
        <Card className="p-12 text-center text-[var(--text-muted)] rounded-2xl border border-[var(--border)]" data-testid="complaints-empty">
          <MessageSquareWarning size={32} className="mx-auto mb-3 opacity-40" />
          Keine Reklamationen{filter !== "all" ? " in diesem Status" : ""}.
        </Card>
      ) : (
        <div className="space-y-4" data-testid="complaints-list">
          {complaints.map((c) => <ComplaintCard key={c.id} c={c} onUpdated={onUpdated} />)}
        </div>
      )}
    </div>
  );
}
